import { cartLinesToCaptures } from "@/domain/cart/cart-line-capture";
import { resolveCartValidity } from "@/domain/cart/cart-validity.resolver";
import { attachReorderValidityCapture } from "@/domain/reorder/reorder.capture";
import {
  canCommitReorder,
  resolveValidityFailureCode,
} from "@/domain/reorder/reorder.gates";
import {
  deriveReorderFailurePresentation,
  deriveReorderSuccessPresentation,
} from "@/domain/reorder/reorder.presentation";
import { transformOrderForReorder, validateReorderCatalog } from "@/domain/reorder/reorder.transform";
import type {
  ReorderBestEffortSummary,
  ReorderDiagnostic,
  ReorderPipelineFailure,
  ReorderPipelineInput,
  ReorderPipelineResult,
} from "@/domain/reorder/reorder.types";

function buildFailure(
  code: ReorderPipelineFailure["code"],
  message: string,
  diagnostics: ReorderDiagnostic[],
  validity?: ReorderPipelineFailure["validity"]
): ReorderPipelineFailure {
  return {
    status: "failure",
    code,
    message,
    diagnostics,
    validity,
    presentation: deriveReorderFailurePresentation(code, diagnostics, validity),
  };
}

function buildSummary(
  lines: ReorderBestEffortSummary["addedItems"],
  transform: ReturnType<typeof transformOrderForReorder>
): ReorderBestEffortSummary {
  return {
    addedItems: lines,
    skippedItems: transform.skippedItems,
    removedCustomizations: transform.removedCustomizations,
    requiresReview: transform.requiresReview || lines.some((line) => line.needsReview === true),
  };
}

/**
 * Dry-run reorder pipeline: catalog → best-effort transform → capture → validity → gate.
 * Does not mutate cart, navigate, or call billing preview.
 */
export function evaluateReorderPipeline(input: ReorderPipelineInput): ReorderPipelineResult {
  const { order, catalog } = input;

  if (order.items.length === 0) {
    return buildFailure("EMPTY_ORDER", "Order has no items to reorder", []);
  }

  const catalogDiagnostics = validateReorderCatalog(order, catalog);
  if (catalogDiagnostics.length > 0) {
    const first = catalogDiagnostics[0];
    return buildFailure(first.code, first.message, catalogDiagnostics);
  }

  const transform = transformOrderForReorder({ order, catalog });
  if (transform.errors.length > 0) {
    const first = transform.errors[0]!;
    return buildFailure(first.code, first.message, transform.errors);
  }

  if (transform.lines.length === 0) {
    return buildFailure(
      "NOTHING_RESTORED",
      "No order lines could be restored from the current menu",
      transform.warnings
    );
  }

  const capturedLines = attachReorderValidityCapture(transform.lines, catalog);
  const validity = resolveCartValidity({
    cartLines: capturedLines,
    catalog,
    captures: cartLinesToCaptures(capturedLines),
  });

  const gateContext = {
    order,
    transform: { ...transform, lines: capturedLines },
    lines: capturedLines,
    validity,
  };

  if (!canCommitReorder(gateContext)) {
    const validityCode = resolveValidityFailureCode(validity.status);
    if (validityCode) {
      return buildFailure(
        validityCode,
        validity.presentation.headline ?? "Cart validity check failed",
        transform.warnings,
        validity
      );
    }

    return buildFailure(
      "NOTHING_RESTORED",
      "Reorder could not be committed",
      transform.warnings,
      validity
    );
  }

  const summary = buildSummary(capturedLines, transform);

  return {
    status: "success",
    lines: capturedLines,
    validity,
    warnings: transform.warnings,
    summary,
    presentation: deriveReorderSuccessPresentation(summary),
  };
}
